import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { QrCode } from "../Home/QrCode";
import { COLORS } from "../Home/constants";
import { explainCodeError, linkUrl, pollRequest, startRequest } from "../../auth/deviceLogin";

// ─────────────────────────────────────────────────────────────────────────────
// The computer's side of "Sign in with a code from your phone": a QR code and a
// short code, valid 2 minutes, while the page asks the server every 2 seconds
// whether the phone approved.
//
//   mode "login"   on the sign-in page (signed out): onApproved(tokens)
//   mode "reauth"  in "Confirm it's you" (signed in): onApproved() — the
//                  server already refreshed this session's check
//
// When the code expires the page stops and offers New code; it never makes
// codes by itself forever. tone: "light" (sign-in pages) or "dark" (Studio).
// ─────────────────────────────────────────────────────────────────────────────

const TONES = {
  light: { text: "var(--fn-navy)", muted: "var(--fn-muted-l)", accent: "var(--fn-teal-ink)", codeBg: "var(--fn-white)", border: "var(--fn-line-l)", danger: "#B42318" },
  dark:  { text: COLORS.text, muted: COLORS.muted, accent: COLORS.cyan, codeBg: COLORS.input, border: COLORS.border, danger: "#E8A0A0" }
};

const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export function PhoneCodePanel({ mode = "login", tone = "light", compact = false, onApproved }) {
  const c = TONES[tone] || TONES.light;
  const [req, setReq] = useState(null);           // { requestSecret, userCode, expiresIn, interval }
  const [state, setState] = useState("loading");  // loading | waiting | denied | expired | error
  const [error, setError] = useState("");
  const [left, setLeft] = useState(0);
  const done = useRef(false);
  const onApprovedRef = useRef(onApproved);
  onApprovedRef.current = onApproved;

  const begin = useCallback(async () => {
    setState("loading"); setError(""); setReq(null);
    try {
      const r = await startRequest(mode);
      setReq(r); setLeft(r.expiresIn); setState("waiting");
    } catch (err) {
      setState("error"); setError(explainCodeError(err, "Couldn't get a code. Please try again."));
    }
  }, [mode]);

  useEffect(() => { begin(); }, [begin]);
  useEffect(() => () => { done.current = true; }, []);

  // Ask every `interval` seconds; the countdown runs on its own clock.
  useEffect(() => {
    if (state !== "waiting" || !req) return undefined;
    const until = Date.now() + req.expiresIn * 1000;
    const tick = setInterval(() => setLeft(Math.max(0, Math.ceil((until - Date.now()) / 1000))), 500);
    let stop = false;
    let timer = null;
    const ask = async () => {
      if (stop) return;
      try {
        const r = await pollRequest(req.requestSecret);
        if (stop || done.current) return;
        if (r.status === "approved") { stop = true; onApprovedRef.current?.(r); return; }
        if (r.status === "denied") { setState("denied"); return; }
        if (r.status === "expired") { setState("expired"); return; }
      } catch { /* a dropped request: ask again */ }
      if (Date.now() > until + 3000) { setState("expired"); return; }
      timer = setTimeout(ask, (req.interval || 2) * 1000);
    };
    timer = setTimeout(ask, (req.interval || 2) * 1000);
    return () => { stop = true; clearTimeout(timer); clearInterval(tick); };
  }, [state, req]);

  const newCode = (
    <Button onClick={begin} startIcon={<RefreshIcon />}
      sx={{ textTransform: "none", fontWeight: 700, color: c.accent, borderRadius: "999px", px: 2 }}>
      New code
    </Button>
  );

  if (state === "loading") {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress sx={{ color: c.accent }} /></Box>;
  }
  if (state === "error") {
    return (
      <Stack spacing={1.5} alignItems="center" sx={{ py: 3, textAlign: "center" }}>
        <Typography role="alert" sx={{ color: c.danger, fontSize: 14.5 }}>{error}</Typography>
        {newCode}
      </Stack>
    );
  }

  const ended = state === "denied" || state === "expired";
  return (
    <Stack spacing={2} alignItems="center" sx={{ textAlign: "center" }}>
      <Stack direction={compact ? "column" : { xs: "column", sm: "row" }} spacing={compact ? 1.5 : 2.5} alignItems="center">
        <Box sx={{ opacity: ended ? 0.2 : 1, transition: "opacity .2s" }} aria-hidden={ended}>
          <QrCode text={linkUrl(req.userCode)} size={168} label="QR code to open the approval page on your phone" />
        </Box>
        <Box sx={{ textAlign: compact ? "center" : { xs: "center", sm: "left" }, maxWidth: compact ? 340 : 240 }}>
          <Typography sx={{ color: c.text, fontSize: 14.5, lineHeight: 1.55 }}>
            On your phone, where you're signed in to Fennira, scan this code, or open{" "}
            <Box component="strong" sx={{ whiteSpace: "nowrap" }}>{window.location.host}/link</Box> and enter:
          </Typography>
          <Typography aria-label={`Code ${req.userCode.split("").join(" ")}`}
            sx={{ mt: 1.25, display: "inline-block", px: 1.75, py: 0.75, borderRadius: "12px", border: `1px solid ${c.border}`,
              bgcolor: c.codeBg, color: c.text, fontFamily: "'JetBrains Mono', ui-monospace, Consolas, monospace",
              fontSize: 28, fontWeight: 700, letterSpacing: "0.12em", opacity: ended ? 0.35 : 1,
              textDecoration: ended ? "line-through" : "none" }}>
            {req.userCode}
          </Typography>
        </Box>
      </Stack>

      <Box role="status" aria-live="polite" sx={{ minHeight: 40 }}>
        {state === "waiting" && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <CircularProgress size={14} thickness={5} sx={{ color: c.accent }} />
            <Typography sx={{ color: c.muted, fontSize: 14 }}>
              Waiting for your phone… {mmss(left)}
            </Typography>
          </Stack>
        )}
        {state === "denied" && (
          <Stack spacing={0.5} alignItems="center">
            <Typography sx={{ color: c.danger, fontSize: 14.5, fontWeight: 600 }}>Refused on your phone.</Typography>
            {newCode}
          </Stack>
        )}
        {state === "expired" && (
          <Stack spacing={0.5} alignItems="center">
            <Typography sx={{ color: c.muted, fontSize: 14.5 }}>This code has expired.</Typography>
            {newCode}
          </Stack>
        )}
      </Box>
      <Typography sx={{ color: c.muted, fontSize: 12.5, lineHeight: 1.5, maxWidth: 400 }}>
        {mode === "login"
          ? "Works without Bluetooth. Your phone shows this computer and asks for your passkey before signing it in."
          : "Your phone shows this computer and asks for your passkey. Then you can continue here."}
      </Typography>
    </Stack>
  );
}
